import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Save, Plus, Trash2, Eye, EyeOff, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import apiClient from '@/utils/api';
import ImageUpload from '@/components/ImageUpload';

export default function PopupSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [emailSettings, setEmailSettings] = useState({
    enabled: false,
    recipient_email: '',
    smtp_configured: false
  });
  const [settings, setSettings] = useState({
    enabled: false,
    title: "Don't Miss Out!",
    subtitle: "Get exclusive updates and offers",
    image_url: "",
    form_fields: [],
    submit_button_text: "Submit",
    success_message: "Thank you! We'll be in touch soon.",
    trigger_type: "delay",
    delay_seconds: 5,
    scroll_percentage: 50,
    show_on_pages: "all",
    specific_pages: [],
    show_frequency: "once_per_session",
    primary_color: "#E12429",
    overlay_color: "rgba(0, 0, 0, 0.7)",
    popup_width: "600px"
  });

  useEffect(() => {
    fetchSettings();
    fetchSubmissions();
    fetchEmailSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await apiClient.get('/admin/popup-settings');
      if (response.data && Object.keys(response.data).length > 0) {
        setSettings(response.data);
      }
    } catch (error) {
      console.error('Error fetching popup settings:', error);
      toast.error('Failed to load popup settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubmissions = async () => {
    try {
      const response = await apiClient.get('/admin/popup-submissions');
      setSubmissions(response.data);
    } catch (error) {
      console.error('Error fetching submissions:', error);
    }
  };

  const fetchEmailSettings = async () => {
    try {
      const response = await apiClient.get('/admin/popup-email-settings');
      setEmailSettings(response.data);
    } catch (error) {
      console.error('Error fetching email settings:', error);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiClient.put('/admin/popup-settings', settings);
      toast.success('Popup settings saved successfully!');
    } catch (error) {
      toast.error('Failed to save popup settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEmailSettings = async () => {
    setSaving(true);
    try {
      await apiClient.put('/admin/popup-email-settings', emailSettings);
      toast.success('Email settings saved successfully!');
    } catch (error) {
      toast.error('Failed to save email settings');
    } finally {
      setSaving(false);
    }
  };

  const addFormField = () => {
    const newField = {
      id: `field_${Date.now()}`,
      name: "",
      label: "",
      type: "text",
      placeholder: "",
      required: true,
      options: [],
      order: settings.form_fields.length
    };
    setSettings({
      ...settings,
      form_fields: [...settings.form_fields, newField]
    });
  };

  const updateFormField = (index, field) => {
    const updatedFields = [...settings.form_fields];
    updatedFields[index] = field;
    setSettings({ ...settings, form_fields: updatedFields });
  };

  const removeFormField = (index) => {
    const updatedFields = settings.form_fields.filter((_, i) => i !== index);
    setSettings({ ...settings, form_fields: updatedFields });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <Link to="/admin" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4">
            <ArrowLeft size={20} className="mr-2" />
            Back to Dashboard
          </Link>
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Popup Settings</h1>
              <p className="text-gray-600 mt-1">Configure website popup with custom form and triggers</p>
            </div>
            <Button 
              onClick={async () => {
                const newEnabled = !settings.enabled;
                setSettings({ ...settings, enabled: newEnabled });
                try {
                  await apiClient.put('/admin/popup-settings', { ...settings, enabled: newEnabled });
                  toast.success(`Popup ${newEnabled ? 'enabled' : 'disabled'} successfully!`);
                } catch (error) {
                  toast.error('Failed to update popup status');
                  setSettings({ ...settings, enabled: !newEnabled });
                }
              }}
              className={settings.enabled ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-400 hover:bg-gray-500'}
            >
              {settings.enabled ? <Eye className="mr-2" size={18} /> : <EyeOff className="mr-2" size={18} />}
              {settings.enabled ? 'Enabled' : 'Disabled'}
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <Tabs defaultValue="design" className="space-y-6">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="design">Design</TabsTrigger>
            <TabsTrigger value="form">Form Fields</TabsTrigger>
            <TabsTrigger value="triggers">Triggers</TabsTrigger>
            <TabsTrigger value="email">Email Settings</TabsTrigger>
            <TabsTrigger value="submissions">Submissions ({submissions.length})</TabsTrigger>
          </TabsList>

          {/* Design Tab */}
          <TabsContent value="design">
            <form onSubmit={handleSave} className="bg-white rounded-lg shadow-md p-6 space-y-6">
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-gray-900">Popup Design</h3>
                
                <div>
                  <Label>Title</Label>
                  <Input
                    value={settings.title}
                    onChange={(e) => setSettings({ ...settings, title: e.target.value })}
                    placeholder="Don't Miss Out!"
                  />
                </div>

                <div>
                  <Label>Subtitle</Label>
                  <Textarea
                    value={settings.subtitle}
                    onChange={(e) => setSettings({ ...settings, subtitle: e.target.value })}
                    placeholder="Get exclusive updates and offers"
                    rows={2}
                  />
                </div>

                <div>
                  <ImageUpload
                    value={settings.image_url}
                    onChange={(url) => setSettings({ ...settings, image_url: url })}
                    label="Popup Banner Image (Recommended: 800x400px)"
                    preview={true}
                  />
                  {settings.image_url && (
                    <div className="mt-3 p-3 bg-blue-50 rounded border border-blue-200">
                      <p className="text-sm text-blue-800">✓ Image will appear at the top of popup</p>
                    </div>
                  )}
                </div>

                <div>
                  <Label>Success Message</Label>
                  <Input
                    value={settings.success_message}
                    onChange={(e) => setSettings({ ...settings, success_message: e.target.value })}
                    placeholder="Thank you! We'll be in touch soon."
                  />
                </div>

                <div>
                  <Label>Submit Button Text</Label>
                  <Input
                    value={settings.submit_button_text}
                    onChange={(e) => setSettings({ ...settings, submit_button_text: e.target.value })}
                    placeholder="Submit"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Primary Color</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="color"
                        value={settings.primary_color}
                        onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                        className="w-20 h-10"
                      />
                      <span className="text-sm text-gray-600">{settings.primary_color}</span>
                    </div>
                  </div>

                  <div>
                    <Label>Popup Width</Label>
                    <Select 
                      value={settings.popup_width} 
                      onValueChange={(value) => setSettings({ ...settings, popup_width: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="500px">Small (500px)</SelectItem>
                        <SelectItem value="600px">Medium (600px)</SelectItem>
                        <SelectItem value="800px">Large (800px)</SelectItem>
                        <SelectItem value="90%">Full Width (90%)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                  <Save size={18} className="mr-2" />
                  {saving ? 'Saving...' : 'Save Settings'}
                </Button>
              </div>
            </form>
          </TabsContent>

          {/* Form Fields Tab */}
          <TabsContent value="form">
            <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold text-gray-900">Form Fields</h3>
                <Button onClick={addFormField} className="bg-green-600 hover:bg-green-700">
                  <Plus size={18} className="mr-2" />
                  Add Field
                </Button>
              </div>

              <div className="space-y-4">
                {settings.form_fields.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No form fields yet. Click "Add Field" to create one.</p>
                ) : (
                  settings.form_fields.map((field, index) => (
                    <div key={field.id} className="border rounded-lg p-4 space-y-3">
                      <div className="flex justify-between items-center">
                        <h4 className="font-semibold text-gray-700">Field {index + 1}</h4>
                        <Button 
                          size="sm" 
                          variant="destructive" 
                          onClick={() => removeFormField(index)}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <Label>Field Name (for data)</Label>
                          <Input
                            value={field.name}
                            onChange={(e) => updateFormField(index, { ...field, name: e.target.value })}
                            placeholder="email"
                          />
                        </div>

                        <div>
                          <Label>Field Label (visible)</Label>
                          <Input
                            value={field.label}
                            onChange={(e) => updateFormField(index, { ...field, label: e.target.value })}
                            placeholder="Email Address"
                          />
                        </div>

                        <div>
                          <Label>Field Type</Label>
                          <Select 
                            value={field.type} 
                            onValueChange={(value) => updateFormField(index, { ...field, type: value })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="text">Text</SelectItem>
                              <SelectItem value="email">Email</SelectItem>
                              <SelectItem value="tel">Phone</SelectItem>
                              <SelectItem value="textarea">Textarea</SelectItem>
                              <SelectItem value="select">Dropdown</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <Label>Placeholder</Label>
                          <Input
                            value={field.placeholder}
                            onChange={(e) => updateFormField(index, { ...field, placeholder: e.target.value })}
                            placeholder="Enter your email"
                          />
                        </div>
                      </div>

                      {field.type === 'select' && (
                        <div>
                          <Label>Options (comma separated)</Label>
                          <Input
                            value={field.options.join(', ')}
                            onChange={(e) => updateFormField(index, { 
                              ...field, 
                              options: e.target.value.split(',').map(opt => opt.trim()).filter(Boolean)
                            })}
                            placeholder="Option 1, Option 2, Option 3"
                          />
                        </div>
                      )}

                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={field.required}
                          onChange={(e) => updateFormField(index, { ...field, required: e.target.checked })}
                          className="rounded"
                        />
                        <Label>Required Field</Label>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="flex justify-end">
                <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                  <Save size={18} className="mr-2" />
                  {saving ? 'Saving...' : 'Save Settings'}
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Triggers Tab */}
          <TabsContent value="triggers">
            <form onSubmit={handleSave} className="bg-white rounded-lg shadow-md p-6 space-y-6">
              <h3 className="text-xl font-semibold text-gray-900">Popup Triggers & Display</h3>

              <div>
                <Label>Trigger Type</Label>
                <Select 
                  value={settings.trigger_type} 
                  onValueChange={(value) => setSettings({ ...settings, trigger_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="delay">Time Delay</SelectItem>
                    <SelectItem value="scroll">Scroll Percentage</SelectItem>
                    <SelectItem value="exit_intent">Exit Intent</SelectItem>
                    <SelectItem value="manual">Manual (via button)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {settings.trigger_type === 'delay' && (
                <div>
                  <Label>Delay (seconds)</Label>
                  <Input
                    type="number"
                    value={settings.delay_seconds}
                    onChange={(e) => setSettings({ ...settings, delay_seconds: parseInt(e.target.value) || 0 })}
                    min="0"
                  />
                </div>
              )}

              {settings.trigger_type === 'scroll' && (
                <div>
                  <Label>Scroll Percentage (%)</Label>
                  <Input
                    type="number"
                    value={settings.scroll_percentage}
                    onChange={(e) => setSettings({ ...settings, scroll_percentage: parseInt(e.target.value) || 0 })}
                    min="0"
                    max="100"
                  />
                </div>
              )}

              <div>
                <Label>Show On Pages</Label>
                <Select 
                  value={settings.show_on_pages} 
                  onValueChange={(value) => setSettings({ ...settings, show_on_pages: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Pages</SelectItem>
                    <SelectItem value="homepage">Homepage Only</SelectItem>
                    <SelectItem value="specific">Specific Pages</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {settings.show_on_pages === 'specific' && (
                <div>
                  <Label>Specific Pages (comma separated paths)</Label>
                  <Input
                    value={settings.specific_pages.join(', ')}
                    onChange={(e) => setSettings({ 
                      ...settings, 
                      specific_pages: e.target.value.split(',').map(p => p.trim()).filter(Boolean)
                    })}
                    placeholder="/about, /services, /contact"
                  />
                </div>
              )}

              <div>
                <Label>Show Frequency</Label>
                <Select 
                  value={settings.show_frequency} 
                  onValueChange={(value) => setSettings({ ...settings, show_frequency: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="once_per_session">Once Per Session</SelectItem>
                    <SelectItem value="once_per_day">Once Per Day</SelectItem>
                    <SelectItem value="always">Every Time</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-end">
                <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                  <Save size={18} className="mr-2" />
                  {saving ? 'Saving...' : 'Save Settings'}
                </Button>
              </div>
            </form>
          </TabsContent>

          {/* Email Settings Tab */}
          <TabsContent value="email">
            <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">Email Notification Settings</h3>
                  <p className="text-sm text-gray-600 mt-1">Configure email notifications for popup form submissions</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Send Email Notifications</span>
                  <input
                    type="checkbox"
                    checked={emailSettings.enabled}
                    onChange={(e) => setEmailSettings({ ...emailSettings, enabled: e.target.checked })}
                    className="w-5 h-5 rounded"
                  />
                </div>
              </div>

              {emailSettings.smtp_configured ? (
                <div className="bg-green-50 border border-green-200 rounded p-4 flex items-start gap-3">
                  <Mail className="text-green-600 mt-1" size={20} />
                  <div>
                    <p className="text-sm font-medium text-green-800">SMTP Configured</p>
                    <p className="text-xs text-green-700 mt-1">Email notifications are ready to use. Configure via Lead Center settings.</p>
                  </div>
                </div>
              ) : (
                <div className="bg-yellow-50 border border-yellow-200 rounded p-4 flex items-start gap-3">
                  <Mail className="text-yellow-600 mt-1" size={20} />
                  <div>
                    <p className="text-sm font-medium text-yellow-800">SMTP Not Configured</p>
                    <p className="text-xs text-yellow-700 mt-1">To enable email notifications, please configure SMTP settings in the Lead Center section.</p>
                  </div>
                </div>
              )}

              <div>
                <Label>Recipient Email Address</Label>
                <p className="text-xs text-gray-500 mb-2">Where should popup form submissions be sent?</p>
                <Input
                  type="email"
                  value={emailSettings.recipient_email}
                  onChange={(e) => setEmailSettings({ ...emailSettings, recipient_email: e.target.value })}
                  placeholder="admin@thecircuit.com"
                />
              </div>

              <div className="bg-gray-50 rounded p-4">
                <h4 className="font-medium text-gray-900 mb-2">Email Preview</h4>
                <div className="text-sm text-gray-600 space-y-1">
                  <p><strong>Subject:</strong> New Popup Form Submission</p>
                  <p><strong>Contains:</strong> All form field data submitted by the user</p>
                  <p><strong>Timestamp:</strong> When the form was submitted</p>
                </div>
              </div>

              <div className="flex justify-end">
                <Button 
                  onClick={handleSaveEmailSettings} 
                  disabled={saving || !emailSettings.recipient_email}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Save size={18} className="mr-2" />
                  {saving ? 'Saving...' : 'Save Email Settings'}
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Submissions Tab */}
          <TabsContent value="submissions">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Form Submissions</h3>
              
              {submissions.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No submissions yet.</p>
              ) : (
                <div className="space-y-4">
                  {submissions.map((submission) => (
                    <div key={submission.id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <span className="text-sm text-gray-500">
                          {new Date(submission.submitted_at).toLocaleString()}
                        </span>
                      </div>
                      <div className="space-y-2">
                        {Object.entries(submission.form_data).map(([key, value]) => (
                          <div key={key} className="flex">
                            <span className="font-semibold text-gray-700 w-32">{key}:</span>
                            <span className="text-gray-600">{value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
